import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useSearchParam } from "react-use";
import { v4 as uuid } from "uuid";
import { useHMSActions } from "@100mslive/react-sdk";
import { Box, Flex, Loading, styled } from "@100mslive/roomkit-react";
import PreviewContainer from "./Preview/PreviewContainer";
import SidePane from "../layouts/SidePane";
import { ErrorDialog } from "../primitives/DialogContent";
import { Header } from "./Header";
import { useSetUiSettings } from "./AppData/useUISettings";
import { useNavigation } from "./hooks/useNavigation";
import { getEntitlementsFromUrlJwt, getUserInfoFromUrlJwt } from "../services/entitlements";
import {
  APP_DATA,
  QUERY_PARAM_AUTH_TOKEN,
  QUERY_PARAM_NAME,
  QUERY_PARAM_PREVIEW_AS_ROLE,
  QUERY_PARAM_SKIP_PREVIEW,
  QUERY_PARAM_SKIP_PREVIEW_HEADFUL,
  UI_SETTINGS,
} from "../common/constants";

/**
 * query params exposed -
 * skip_preview=true => used by recording and streaming service, skips preview and directly joins
 *                      header and footer don't show up in this case
 * skip_preview_headful=true => used by automation testing to skip preview without impacting the UI
 * name=abc => gives the initial name for the peer joining
 * auth_token=123 => uses the passed in token to join instead of fetching from token endpoint
 * ui_mode=activespeaker => lands in active speaker mode after joining the room
 */

const env = process.env.REACT_APP_ENV;
const PreviewScreen = React.memo(() => {
  const navigate = useNavigation();
  const hmsActions = useHMSActions();
  const [, setIsHeadless] = useSetUiSettings(UI_SETTINGS.isHeadless);
  const { roomId: urlRoomId, role: userRole } = useParams(); // from the url
  const [token, setToken] = useState(null);
  const [error, setError] = useState({ title: "", body: "" });
  // way to skip preview for automated tests, beam recording and streaming
  const beamInToken = useSearchParam("token") === "beam_recording"; // old format to remove
  let skipPreview = useSearchParam(QUERY_PARAM_SKIP_PREVIEW) === "true";
  // use this field to join directly for quick testing while in local
  const directJoinHeadfulFromEnv =
    process.env.REACT_APP_HEADLESS_JOIN === "true";
  const directJoinHeadful =
    useSearchParam(QUERY_PARAM_SKIP_PREVIEW_HEADFUL) === "true" ||
    directJoinHeadfulFromEnv;
  skipPreview = skipPreview || beamInToken || directJoinHeadful;
  const initialName =
    useSearchParam(QUERY_PARAM_NAME) || (skipPreview ? "Beam" : "");
  const previewAsRole = useSearchParam(QUERY_PARAM_PREVIEW_AS_ROLE);
  let authToken = useSearchParam(QUERY_PARAM_AUTH_TOKEN);
  useEffect(() => {
    if (authToken) {
      try {
        sessionStorage.setItem("last_auth_token", authToken);
      } catch (e) {
        console.warn("Error at <PreviewScreen>:", e);
      }
    }
  }, [authToken]);

  if (!authToken && typeof window !== "undefined") {
    try {
      const storedToken = sessionStorage.getItem("last_auth_token");
      if (storedToken) {
        authToken = storedToken;
      }
    } catch (e) {
      console.log(e)
    }
  }
  
  // Store entitlements and role in sessionStorage when auth_token is present
  // AppData component will load it into HMS Store after initialization
  useEffect(() => {
    if (authToken) {
      const entitlements = getEntitlementsFromUrlJwt();
      const userInfo = getUserInfoFromUrlJwt();
      try {
        sessionStorage.setItem('hms_entitlements', JSON.stringify(entitlements));
        if (userInfo && userInfo.role) {
          sessionStorage.setItem('hms_role', userInfo.role);
        }
      } catch (error) {
        console.error('[PreviewScreen] Error storing entitlements/role:', error);
      }
    }
  }, [authToken]);
  
  useEffect(() => {
    if (authToken) {
      setToken(authToken);
    } else {
      setError({
        title: "Token não encontrado",
        body: "É necessário um token de autenticação para acessar a sala."
      });
    }
  }, [authToken]);

  const onJoin = () => {
    !directJoinHeadful && setIsHeadless(skipPreview);
    let meetingURL = `/meeting/${urlRoomId}`;
    if (userRole) {
      meetingURL += `/${userRole}`;
    }
    navigate(meetingURL);
  };

  if (error.title) {
    return <ErrorDialog title={error.title}>{error.body}</ErrorDialog>;
  }
  return (
    <Flex direction="column" css={{ size: "100%", overflow: "hidden" }}>
      <Box
        css={{ 
          h: "$18", 
          flexShrink: 0,
          zIndex: 10,
          "@md": { h: "$17" } 
        }}
        data-testid="header"
      >
        <Header isPreview={true} />
      </Box>
      <Flex
        css={{ 
          flex: "1 1 0", 
          minHeight: 0,
          position: "relative", 
          overflowY: "auto",
          py: "$10",
          "@md": {
            py: "$4",
          },
        }}
        justify="center"
        align="start"
      >
        {token ? (
          <>
            <PreviewContainer
              initialName={initialName}
              skipPreview={skipPreview}
              env={env}
              onJoin={onJoin}
              token={token}
              asRole={previewAsRole}
            />
          </>
        ) : (
          <Loading size={100} />
        )}
        <SidePane
          css={{
            position: "unset",
            mr: "$10",
            "@lg": { position: "fixed", mr: "$0" },
          }}
        />
      </Flex>
    </Flex>
  );
});

const Link = styled("a", {
  color: "#2f80e1",
});

export const ErrorWithSupportLink = errorMessage => (
  <div>
    {errorMessage} If you think this is a mistake on our side, please create{" "}
    <Link
      target="_blank"
      href="https://github.com/100mslive/100ms-web/issues"
      rel="noreferrer"
    >
      an issue
    </Link>{" "}
    or reach out over{" "}
    <Link
      target="_blank"
      href="https://discord.com/invite/kGdmszyzq2"
      rel="noreferrer"
    >
      Discord
    </Link>
    .
  </div>
);

export default PreviewScreen;
